import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  readStudentProfile,
  getProgressSummary,
  getInProgressTopics,
  getGroupProgress,
  getContinueLearningTopic,
  getRevisionQueue,
  getWeeklyStudyMinutes,
  getTotalStudyMinutes,
  getRecommendedTopics,
  getQuizActivity,
} from '../utils/studentProfileStorage';
import {
  getNumerologyProgressSummary,
  getNumerologyContinueLearning,
  getNumerologyGroupProgress,
  getNumerologyRecommendedTopics,
  getNumerologyInProgressTopics,
  filterNumerologyBookmarks,
  filterNumerologyHighlights,
  filterJyotishaBookmarks,
  filterJyotishaHighlights,
  filterJyotishaRevisionQueue,
  filterNumerologyRevisionQueue,
  filterSchoolBookmarks,
  filterSchoolHighlights,
  filterSchoolRevisionQueue,
} from '../utils/numerologyProgress';

/** React hook for student profile dashboard data. */
export function useStudentProfile() {
  const { user, loading: authLoading } = useAuth();
  const [state, setState] = useState(() => readStudentProfile());

  const refresh = useCallback(() => {
    setState(readStudentProfile());
  }, []);

  useEffect(() => {
    const onUpdate = () => refresh();
    window.addEventListener('student-profile-updated', onUpdate);
    window.addEventListener('storage', onUpdate);
    return () => {
      window.removeEventListener('student-profile-updated', onUpdate);
      window.removeEventListener('storage', onUpdate);
    };
  }, [refresh]);

  useEffect(() => {
    if (authLoading || !user?.id) return;
    refresh();
  }, [user?.id, authLoading, refresh]);

  return {
    state,
    refresh,
    profile: state.profile,
    topicProgress: state.topicProgress,
    highlights: state.highlights,
    bookmarks: state.bookmarks,
    goals: state.goals,
    analytics: state.analytics,
    recentTopics: state.recentTopics,
    progressSummary: getProgressSummary(),
    inProgressTopics: getInProgressTopics(),
    groupProgress: getGroupProgress(),
    continueLearning: getContinueLearningTopic(),
    revisionQueue: getRevisionQueue(),
    weeklyStudy: getWeeklyStudyMinutes(),
    totalStudyMinutes: getTotalStudyMinutes(),
    recommendedTopics: getRecommendedTopics(4),
    quizActivity: getQuizActivity(),
    numerologyProgressSummary: getNumerologyProgressSummary(),
    numerologyContinueLearning: getNumerologyContinueLearning(),
    numerologyGroupProgress: getNumerologyGroupProgress(),
    numerologyRecommendedTopics: getNumerologyRecommendedTopics(4),
    numerologyInProgressTopics: getNumerologyInProgressTopics(),
    jyotishaBookmarks: filterJyotishaBookmarks(state.bookmarks),
    numerologyBookmarks: filterNumerologyBookmarks(state.bookmarks),
    jyotishaHighlights: filterJyotishaHighlights(state.highlights),
    numerologyHighlights: filterNumerologyHighlights(state.highlights),
    jyotishaRevisionQueue: filterJyotishaRevisionQueue(getRevisionQueue()),
    numerologyRevisionQueue: filterNumerologyRevisionQueue(getRevisionQueue()),
    jaiminiBookmarks: filterSchoolBookmarks(state.bookmarks, 'jaimini'),
    bnnBookmarks: filterSchoolBookmarks(state.bookmarks, 'bnn'),
    kpBookmarks: filterSchoolBookmarks(state.bookmarks, 'kp'),
    lalKitabBookmarks: filterSchoolBookmarks(state.bookmarks, 'lal-kitab'),
    jaiminiHighlights: filterSchoolHighlights(state.highlights, 'jaimini'),
    bnnHighlights: filterSchoolHighlights(state.highlights, 'bnn'),
    kpHighlights: filterSchoolHighlights(state.highlights, 'kp'),
    lalKitabHighlights: filterSchoolHighlights(state.highlights, 'lal-kitab'),
    jaiminiRevisionQueue: filterSchoolRevisionQueue(getRevisionQueue(), 'jaimini'),
    bnnRevisionQueue: filterSchoolRevisionQueue(getRevisionQueue(), 'bnn'),
    kpRevisionQueue: filterSchoolRevisionQueue(getRevisionQueue(), 'kp'),
    lalKitabRevisionQueue: filterSchoolRevisionQueue(getRevisionQueue(), 'lal-kitab'),
  };
}
